import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw, Plus, Trash2 } from 'lucide-react';
import API_BASE from '../api';

const BRAND = '#c1009d';

function sortTermsChronologically(items) {
  const MONTHS = {
    january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
    july: 6, august: 7, september: 8, october: 9, november: 10, december: 11
  };
  const endDateMs = (label) => {
    const s = String(label || '').trim();
    if (!s) return null;
    let m = s.match(/^([A-Za-z]+)\s+(\d{4})$/);
    if (m) {
      const mi = MONTHS[String(m[1]).toLowerCase()];
      const y = Number(m[2]);
      if (mi == null || !Number.isFinite(y)) return null;
      return new Date(y, mi + 1, 0).getTime();
    }
    m = s.match(/^([A-Za-z]+)$/);
    if (m) {
      const mi = MONTHS[String(m[1]).toLowerCase()];
      if (mi == null) return null;
      const now = new Date();
      const y = mi < now.getMonth() ? now.getFullYear() + 1 : now.getFullYear();
      return new Date(y, mi + 1, 0).getTime();
    }
    return null;
  };
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  return [...list].sort((a, b) => {
    const ta = endDateMs(a.term_start_date);
    const tb = endDateMs(b.term_start_date);
    if (ta != null && tb != null && ta !== tb) return ta - tb;
    if (ta != null && tb == null) return -1;
    if (ta == null && tb != null) return 1;
    return String(a.term_start_date || '').localeCompare(String(b.term_start_date || ''));
  });
}

function ConfirmModal({ open, title, message, confirmLabel, cancelLabel = 'Cancel', danger, onConfirm, onCancel }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.25rem',
      }}
      role="presentation"
      onClick={onCancel}
    >
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.45)' }} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 420,
          background: '#fff',
          borderRadius: 14,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '1.35rem 1.35rem 0.75rem' }}>
          <h2 id="confirm-modal-title" style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#111827' }}>
            {title}
          </h2>
          {message ? (
            <p style={{ margin: '0.75rem 0 0', fontSize: '0.92rem', lineHeight: 1.55, color: '#4b5563' }}>{message}</p>
          ) : null}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.65rem',
            padding: '1rem 1.35rem 1.25rem',
            background: '#f9fafb',
            borderTop: '1px solid #f3f4f6',
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.88rem',
              fontWeight: 600,
              borderRadius: 8,
              border: '1px solid #d1d5db',
              background: '#fff',
              color: '#374151',
              cursor: 'pointer',
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.88rem',
              fontWeight: 600,
              borderRadius: 8,
              border: 'none',
              background: danger ? '#dc2626' : BRAND,
              color: '#fff',
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Card({ title, subtitle, children, right }) {
  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div className="card-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '1rem' }}>{title}</h2>
            {subtitle ? <p className="text-sm text-muted" style={{ margin: '0.25rem 0 0' }}>{subtitle}</p> : null}
          </div>
          {right || null}
        </div>
        <div style={{ marginTop: '0.85rem' }}>{children}</div>
      </div>
    </div>
  );
}

function Row({ children }) {
  return <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>{children}</div>;
}

function TextInput(props) {
  return (
    <input
      {...props}
      className="form-input"
      style={{
        width: props?.style?.width || 320,
        maxWidth: '100%',
        ...props.style,
      }}
    />
  );
}

function MiniBtn({ children, danger, ...rest }) {
  return (
    <button
      type="button"
      {...rest}
      className={`btn btn-sm ${danger ? 'btn-danger' : 'btn-secondary'}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...rest.style }}
    >
      {children}
    </button>
  );
}

function ListItem({ left, right }) {
  return (
    <div
      style={{
        padding: '0.55rem 0.65rem',
        border: '1px solid var(--gray-200)',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.75rem',
        background: '#fff',
      }}
    >
      <div style={{ minWidth: 0 }}>{left}</div>
      <div style={{ flexShrink: 0 }}>{right}</div>
    </div>
  );
}

export default function Lookups() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courses, setCourses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [termDrafts, setTermDrafts] = useState({});
  const [confirm, setConfirm] = useState(null); // { title, message, danger, confirmLabel, onConfirm }

  const [newCourse, setNewCourse] = useState('');
  const [newTerm, setNewTerm] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/api/lookups`);
      setCourses(res.data.courses || []);
      const nextTerms = sortTermsChronologically(res.data.terms || []);
      setTerms(nextTerms);
      const drafts = {};
      for (const t of nextTerms) {
        drafts[t.id] = {
          term_start_date: t.term_start_date || '',
          start_date: t.start_date || '',
          end_date: t.end_date || '',
        };
      }
      setTermDrafts(drafts);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addCourse = async () => {
    const name = String(newCourse || '').trim();
    if (!name) return;
    try {
      await axios.post(`${API_BASE}/api/lookups/courses`, { name });
      setNewCourse('');
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.error || e.message || 'Failed to add course');
    }
  };

  const deleteCourse = async (id, name) => {
    setConfirm({
      title: `Delete course “${name}”?`,
      message: 'This will remove it from the admin and student dropdowns.',
      danger: true,
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_BASE}/api/lookups/courses/${id}`);
          await fetchAll();
        } catch (e) {
          alert(e.response?.data?.error || e.message || 'Failed to delete course');
        } finally {
          setConfirm(null);
        }
      }
    });
  };

  const addTerm = async () => {
    const term_start_date = String(newTerm || '').trim();
    if (!term_start_date) return;
    try {
      await axios.post(`${API_BASE}/api/lookups/terms`, { term_start_date });
      setNewTerm('');
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.error || e.message || 'Failed to add term date');
    }
  };

  const saveTerm = async (id) => {
    const draft = termDrafts[id] || {};
    const term_start_date = String(draft.term_start_date || '').trim();
    const start_date = String(draft.start_date || '').trim();
    const end_date = String(draft.end_date || '').trim();
    if (!term_start_date) {
      alert('Term label is required');
      return;
    }
    try {
      await axios.patch(`${API_BASE}/api/lookups/terms/${id}`, { term_start_date, start_date, end_date });
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.error || e.message || 'Failed to save term');
    }
  };

  const deleteTerm = async (id, label) => {
    setConfirm({
      title: `Delete term date “${label}”?`,
      message: 'This will remove it from the admin and student dropdowns.',
      danger: true,
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_BASE}/api/lookups/terms/${id}`);
          await fetchAll();
        } catch (e) {
          alert(e.response?.data?.error || e.message || 'Failed to delete term date');
        } finally {
          setConfirm(null);
        }
      }
    });
  };

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1 className="page-title">Courses & Terms</h1></div>
        <div className="card">
          <div className="card-body flex gap-md items-center">
            <div className="loading-spinner"></div>
            <span>Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="page-header"><h1 className="page-title">Courses & Terms</h1></div>
        <div className="card">
          <div className="card-body">
            <p style={{ color: 'var(--error)' }}>{error}</p>
            <button type="button" className="btn btn-secondary" onClick={fetchAll}>
              <RefreshCw size={16} /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ConfirmModal
        open={!!confirm}
        title={confirm?.title || 'Confirm'}
        message={confirm?.message || ''}
        confirmLabel={confirm?.confirmLabel || 'Confirm'}
        danger={!!confirm?.danger}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm?.onConfirm?.()}
      />
      <div className="page-header flex justify-between items-center" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 className="page-title">Courses & Terms</h1>
          <p className="page-subtitle">Manage Airtable lookup tables: `Courses`, `Term Dates`.</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={fetchAll}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      <Card
        title="Courses"
        subtitle="These are the classes/program names shown in the admin and student form."
        right={(
          <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#111827' }}>
            {courses.length} course{courses.length === 1 ? '' : 's'}
          </span>
        )}
      >
        <Row>
          <TextInput
            value={newCourse}
            onChange={(e) => setNewCourse(e.target.value)}
            placeholder="Add a course (e.g. ELA)"
            onKeyDown={(e) => { if (e.key === 'Enter') addCourse(); }}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={addCourse}
            style={{ background: BRAND, borderColor: BRAND, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Plus size={16} /> Add
          </button>
        </Row>

        <div style={{ marginTop: '0.85rem', display: 'grid', gap: 10 }}>
          {courses.map((c) => (
            <ListItem
              key={c.id}
              left={<span style={{ fontWeight: 700 }}>{c.name}</span>}
              right={
                <MiniBtn danger onClick={() => deleteCourse(c.id, c.name)}>
                  <Trash2 size={14} /> Delete
                </MiniBtn>
              }
            />
          ))}
        </div>
      </Card>

      <Card
        title="Term dates"
        subtitle="Parents/admin select from these term labels. You can also set actual start/end dates used for matching windows."
        right={(
          <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#111827' }}>
            {terms.length} term{terms.length === 1 ? '' : 's'}
          </span>
        )}
      >
        <Row>
          <TextInput
            value={newTerm}
            onChange={(e) => setNewTerm(e.target.value)}
            placeholder="Add a term date (e.g. September 2026)"
            onKeyDown={(e) => { if (e.key === 'Enter') addTerm(); }}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={addTerm}
            style={{ background: BRAND, borderColor: BRAND, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Plus size={16} /> Add
          </button>
        </Row>

        <div style={{ marginTop: '0.85rem', display: 'grid', gap: 10 }}>
          {terms.map((t) => (
            <ListItem
              key={t.id}
              left={(
                <div style={{ display: 'grid', gap: 8 }}>
                  <TextInput
                    value={termDrafts[t.id]?.term_start_date || ''}
                    onChange={(e) => setTermDrafts((prev) => ({
                      ...prev,
                      [t.id]: { ...(prev[t.id] || {}), term_start_date: e.target.value }
                    }))}
                    placeholder="Term label (e.g. May 2026)"
                    style={{ width: 320 }}
                  />
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ display: 'grid', gap: 4 }}>
                      <label style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Range start</label>
                      <input
                        type="date"
                        className="form-input"
                        value={termDrafts[t.id]?.start_date || ''}
                        onChange={(e) => setTermDrafts((prev) => ({
                          ...prev,
                          [t.id]: { ...(prev[t.id] || {}), start_date: e.target.value }
                        }))}
                        style={{ width: 170 }}
                      />
                    </div>
                    <div style={{ display: 'grid', gap: 4 }}>
                      <label style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Range end</label>
                      <input
                        type="date"
                        className="form-input"
                        value={termDrafts[t.id]?.end_date || ''}
                        onChange={(e) => setTermDrafts((prev) => ({
                          ...prev,
                          [t.id]: { ...(prev[t.id] || {}), end_date: e.target.value }
                        }))}
                        style={{ width: 170 }}
                      />
                    </div>
                  </div>
                </div>
              )}
              right={
                <div style={{ display: 'flex', gap: 8 }}>
                  <MiniBtn onClick={() => saveTerm(t.id)}>Save</MiniBtn>
                  <MiniBtn danger onClick={() => deleteTerm(t.id, t.term_start_date)}>
                    <Trash2 size={14} /> Delete
                  </MiniBtn>
                </div>
              }
            />
          ))}
        </div>
      </Card>
    </div>
  );
}

